import { useCallback, useEffect, useState } from 'react';
import { verifyPassword, isGateDisabledAccidentally } from './coachPasswords';

// 教练密码门 session 钩子
// - 通过校验后，写入 localStorage：{ label, grantedAt, expiresAt }
// - 默认 30 天免输（VALID_DAYS）
// - 校验过期会自动登出
//
// 注意：这是客户端"软"密码门，技术上能绕（F12 看源码），
// 但对 < 10 人小范围试用足够防君子；真正的安全在部署层做
// （Cloudflare Access / Vercel Password Protection 等）

const STORAGE_KEY  = 'lol_coach_gate_session_v1';
const VALID_DAYS   = 30;

const readSession = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.expiresAt) return null;
    if (Date.now() > obj.expiresAt) return null;
    return obj;
  } catch { return null; }
};

const writeSession = (label) => {
  const now = Date.now();
  const obj = {
    label,
    grantedAt: now,
    expiresAt: now + VALID_DAYS * 24 * 3600 * 1000,
  };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(obj)); } catch {}
  return obj;
};

const clearSession = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
};

const useCoachGate = () => {
  const [session, setSession] = useState(() => readSession());
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);

  // 应急后门：所有 hash 都为空说明用户还没配置，直接放行（避免锁死自己）
  const gateBypass = isGateDisabledAccidentally();

  // 校验密码：成功返回 true 并自动建立 session
  const submitPassword = useCallback(async (pwd) => {
    setError('');
    if (!pwd || !pwd.trim()) {
      setError('请输入密码');
      return false;
    }
    setVerifying(true);
    try {
      const found = await verifyPassword(pwd);
      if (!found) {
        setError('密码不正确');
        setAttempts((n) => n + 1);
        return false;
      }
      const sess = writeSession(found.label);
      setSession(sess);
      setAttempts(0);
      return true;
    } finally {
      setVerifying(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setSession(null);
  }, []);

  // 每分钟检查一次过期（处理跨天 / 浏览器后台挂起）
  useEffect(() => {
    if (!session) return undefined;
    const t = setInterval(() => {
      if (Date.now() > session.expiresAt) {
        clearSession();
        setSession(null);
      }
    }, 60 * 1000);
    return () => clearInterval(t);
  }, [session]);

  const isAuthed = gateBypass || Boolean(session);

  return {
    isAuthed,
    session,         // { label, grantedAt, expiresAt } 或 null
    verifying,
    error,
    attempts,        // 错误尝试次数，UI 用来显示「开发者工具」按钮
    gateBypass,      // true 表示因为未配置密码而自动放行
    submitPassword,
    logout,
  };
};

export default useCoachGate;
